import React, { useEffect, useState, useMemo } from 'react';
import { Table, Button, App, Card, Tabs, Tag, Typography, Input, Flex } from 'antd';
import { MailOutlined, ReloadOutlined, SendOutlined, CheckCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../redux/appState';
import { makeRequest } from '../../../helpers/axios-manipulator';
import dayjs from 'dayjs';

const { Text } = Typography;

const Emails: React.FC = () => {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<any[]>([]);
  const [onboarding, setOnboarding] = useState<any[]>([]);
  const [orderSearch, setOrderSearch] = useState('');
  const [onboardingSearch, setOnboardingSearch] = useState('');
  const user = useSelector((state: RootState) => state.user.user);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [kpiRes, onboardingRes] = await Promise.all([
        makeRequest({
          origin: window.location.origin,
          method: 'get',
          url: `/kpiData?rootUser=true&userId=${user.rep_id || user.id || ''}&contract_id=${(user as any).contract_id ?? ''}`,
        }),
        makeRequest({
          origin: window.location.origin,
          method: 'get',
          url: '/api-onboarding',
        })
      ]);
      setOrders(Array.isArray(kpiRes?.orders) ? kpiRes.orders : []);
      setOnboarding(Array.isArray(onboardingRes) ? onboardingRes : []);
    } catch (err) {
      console.error('Error fetching email data:', err);
      message.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user]);

  const handleResendReceipt = async (orderId: string, targets: string = 'all') => {
    try {
      const response = await makeRequest({
        origin: window.location.origin,
        method: 'post',
        url: '/api/admin/resend-receipt',
        data: { orderId, targets }
      });
      if (response.success) {
        message.success(`Receipt resent to ${targets === 'all' ? 'all parties' : targets} successfully`);
      } else {
        message.error(response.error || 'Failed to resend receipt');
      }
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Error resending receipt');
    }
  };

  const filteredOrders = useMemo(() => {
    let result: any[] = [];
    
    orders.forEach(order => {
      // 1. Collector Email
      result.push({
        ...order,
        email_key: `${order.id}-collector`,
        recipient: order.owner_full_name || order.collector_username || 'Collector',
        recipient_email: order.owner_email || 'N/A',
        role: 'Collector',
        type: 'Receipt'
      });

      // 2. Artist Email
      result.push({
        ...order,
        email_key: `${order.id}-artist`,
        recipient: order.artist_display_name || order.artist_username || 'Artist',
        recipient_email: 'Artist Notification', // We don't have artist email in this query, but we know it's sent
        role: 'Artist',
        type: 'Sale Notification'
      });

      // 3. Representative Emails
      if (order.rep_usernames) {
        const reps = order.rep_usernames.split(';').filter(Boolean);
        reps.forEach((rep: string, idx: number) => {
          result.push({
            ...order,
            email_key: `${order.id}-rep-${idx}`,
            recipient: rep.trim(),
            recipient_email: 'Commission Alert',
            role: 'Representative',
            type: 'Commission'
          });
        });
      }
    });

    result.sort((a, b) => (parseInt(b.id) || 0) - (parseInt(a.id) || 0));

    if (orderSearch) {
      const low = orderSearch.toLowerCase();
      result = result.filter(o => 
        o.id.toString().includes(low) || 
        (o.recipient || '').toLowerCase().includes(low) ||
        (o.role || '').toLowerCase().includes(low) ||
        (o.recipient_email || '').toLowerCase().includes(low)
      );
    }
    return result;
  }, [orders, orderSearch]);

  const filteredOnboarding = useMemo(() => {
    let result = [...onboarding];
    result.sort((a, b) => dayjs(b.updated_at).unix() - dayjs(a.updated_at).unix());
    if (onboardingSearch) {
      const low = onboardingSearch.toLowerCase();
      result = result.filter(o => 
        (o.username || '').toLowerCase().includes(low) || 
        (o.notification_email || '').toLowerCase().includes(low)
      );
    }
    return result;
  }, [onboarding, onboardingSearch]);

  const orderColumns = [
    {
      title: 'Order ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      sorter: (a: any, b: any) => (parseInt(a.id) || 0) - (parseInt(b.id) || 0),
    },
    {
      title: 'Date',
      dataIndex: 'order_date',
      key: 'order_date',
      width: 160,
      render: (val: any) => dayjs(val).format('MMM D, YYYY HH:mm'),
    },
    {
      title: 'Recipient',
      key: 'recipient',
      render: (row: any) => (
        <Flex vertical gap={0}>
          <Text strong>{row.recipient}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>{row.recipient_email}</Text>
        </Flex>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (role: string) => {
        let color = 'blue';
        if (role === 'Artist') color = 'purple';
        if (role === 'Representative') color = 'cyan';
        return <Tag color={color}>{role.toUpperCase()}</Tag>;
      }
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Status',
      key: 'status',
      width: 150,
      render: () => (
        <Flex gap="small">
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
          <Text type="success">Delivered</Text>
        </Flex>
      )
    },
    {
      title: 'Action',
      key: 'action',
      width: 150,
      render: (_: any, record: any) => {
        let target = 'all';
        if (record.role === 'Collector') target = 'collector';
        if (record.role === 'Artist') target = 'artist';
        if (record.role === 'Representative') target = 'reps';
        
        return (
          <Button 
            type="link" 
            icon={<MailOutlined />} 
            onClick={() => handleResendReceipt(record.id, target)}
          >
            Resend
          </Button>
        );
      },
    },
  ];

  const onboardingColumns = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Email',
      dataIndex: 'notification_email',
      key: 'notification_email',
    },
    {
      title: 'Status',
      dataIndex: 'notification_status',
      key: 'notification_status',
      render: (status: string) => {
        let color = 'default';
        if (status === 'SENT') color = 'green';
        if (status === 'COMPLETE') color = 'purple';
        if (status === 'OPEN') color = 'orange';
        return <Tag color={color}>{status}</Tag>;
      }
    },
    {
      title: 'Last Sent',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (val: any) => val ? dayjs(val).format('MMM D, YYYY HH:mm') : 'Never',
    },
    {
        title: 'Action',
        key: 'action',
        render: (_: any, record: any) => (
          <Button 
            type="link" 
            icon={<SendOutlined />} 
            disabled={record.notification_status === 'COMPLETE'}
            onClick={() => message.info('Please use the Onboarding tool to re-invite users')}
          >
            Re-invite
          </Button>
        ),
      },
  ];

  return (
    <div style={{ width: '100%' }}>
      <Card 
        title="Transactional Email Tracking" 
        extra={<Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>Refresh</Button>}
      >
        <Tabs 
          defaultActiveKey="orders"
          items={[
            {
              key: 'orders',
              label: <Flex gap="small"><MailOutlined />Order Receipts</Flex>,
              children: (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <Input
                      placeholder="Search Orders..."
                      prefix={<SearchOutlined />}
                      value={orderSearch}
                      onChange={e => setOrderSearch(e.target.value)}
                      style={{ width: 300 }}
                      allowClear
                    />
                  </div>
                  <Table 
                    dataSource={filteredOrders} 
                    columns={orderColumns} 
                    loading={loading} 
                    rowKey="email_key" 
                    size="small"
                    pagination={{ pageSize: 15 }}
                  />
                </>
              )
            },
            {
              key: 'onboarding',
              label: <Flex gap="small"><SendOutlined />Onboarding Invites</Flex>,
              children: (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <Input
                      placeholder="Search Onboarding..."
                      prefix={<SearchOutlined />}
                      value={onboardingSearch}
                      onChange={e => setOnboardingSearch(e.target.value)}
                      style={{ width: 300 }}
                      allowClear
                    />
                  </div>
                  <Table 
                    dataSource={filteredOnboarding} 
                    columns={onboardingColumns} 
                    loading={loading} 
                    rowKey="id" 
                    size="small"
                    pagination={{ pageSize: 15 }}
                  />
                </>
              )
            }
          ]}
        />
      </Card>
    </div>
  );
};

export default Emails;

